import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ItemDiv } from './styles';
import { COMMENT_TYPE, TASK_PRIORITY } from '../../global/constants';
import { TaskGroupInterface } from '../../interfaces/TaskInterface';
import { trackAnalyticActivity } from '../../services/analyticsService';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../services/taskServices';
import { TASK_ANALYTICS } from '../../global/analyticsConstants';
import Modal from '../modals/modal';
import { Dropdown, DropdownDivider, DropdownItem } from '../dropdown';
import { menuItems } from '../../helpers/list';
import { getRawComment, isEmpty, renderStatusIconName } from '../../helpers/common';
import AssignBoxV2 from '../dropdowns/assignPopup';
import { useTaskAssignActions } from '../dropdowns/assignPopup/assignActions';
import { TaskModel } from '../../mobx/models/task';
import LabelDropdownV2 from '../dropdowns/labelDropdown';
import { ProjectModel } from '../../mobx/models/project';
import CalendarDropdownMenuV2 from '../calendarPopUp/calendarDropdownMenuV2';
import { Milestone } from '../../mobx/interfaces/project';
import { rootStore } from '../../mobx/rootStore';
import { nanoid } from 'nanoid';
import CreateTaskModalV2 from '../modals/createTaskModal';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateModel } from '../../mobx/models/projectTemplate';

interface DropdownRef {
  closeDropdown: () => void;
}

interface ContextMenuContentProps {
  taskData: TaskModel;
  loadData: () => void;
  currentProject?: ProjectModel | ProjectTemplateModel;
  setCurrentActiveTask?: (taskId: string) => void;
  onClickView?: (task: TaskModel) => void;
  onClickClone?: (task: TaskModel) => void;
  onClickEdit?: (task: TaskModel) => void;
  onClickDelete?: (task: TaskModel) => void;
  onUpdateStatus?: (status: StatusListInterface, previousStatus: StatusListInterface) => void;
  isMyTask?: boolean;
  statusList: StatusListInterface[];
  isRecurringTask?: boolean;
  isSubTask?: boolean;
  isMilestonePage?: boolean;
  isTemplateProject?: boolean;
  isView?: boolean;
  contextMenuRef: React.RefObject<DropdownRef | null>;
  onOpenChange: (value: boolean) => void;
  setOpenCreateTaskModel: (value: boolean) => void;
}

const ContextMenuContent: React.FC<ContextMenuContentProps> = observer(
  ({
    taskData,
    currentProject,
    loadData,
    setCurrentActiveTask,
    onClickView,
    onClickClone,
    onClickEdit,
    onClickDelete,
    onUpdateStatus,
    isMyTask = false,
    statusList,
    isRecurringTask,
    isSubTask = false,
    isMilestonePage = false,
    isTemplateProject = false,
    isView = false,
    contextMenuRef,
    onOpenChange,
    setOpenCreateTaskModel
  }) => {
    const [calendarDate, setCalendarDate] = useState(new Date());
    const { currentUser } = rootStore;
    const assignActions = useTaskAssignActions(taskData, {
      isRecurring: isRecurringTask,
      isProjectTemplate: isTemplateProject
    });
    const currentStatus =
      statusList?.find((x) => x['_id'] === taskData?.statusId) || currentProject?.defaultStatus || statusList?.[0];
    const calendarDropdownRef = useRef<DropdownRef>(null);

    useEffect(() => {
      if (taskData?.dueDate) {
        setCalendarDate(new Date(taskData.dueDate));
      }
    }, [taskData?.dueDate]);

    const closeContextModal = useCallback(
      (isAssigneeSection: boolean = false) => {
        if (currentProject?.multiAssignee && isAssigneeSection) {
          return;
        }
        if (setCurrentActiveTask) {
          setCurrentActiveTask('');
          onOpenChange(false);
        }
      },
      [currentProject?.multiAssignee, onOpenChange, setCurrentActiveTask]
    );

    const closeAllDropdowns = useCallback(() => {
      closeContextModal();
      contextMenuRef.current?.closeDropdown();
      calendarDropdownRef.current?.closeDropdown();
    }, [closeContextModal, contextMenuRef]);

    const onClickUpdateStatus = useCallback(
      (status: StatusListInterface, previousStatus: StatusListInterface) => {
        if (onUpdateStatus) onUpdateStatus(status, previousStatus);
        closeAllDropdowns();
      },
      [closeAllDropdowns, onUpdateStatus]
    );

    const onClickGroup = useCallback(
      async (group: TaskGroupInterface) => {
        if (!currentUser) return;
        if (isTemplateProject) {
          taskData?.setGroup(group?.id);
          return;
        }
        if (taskData?.groupId !== group?.id) {
          const previousGroup = currentProject?.taskGroups?.find((x) => x?.id === taskData?.groupId);
          const payloadTask = { groupId: group?.id, projectId: taskData?.projectId };
          const result = await updateTaskDetails(taskData?.id, payloadTask);
          const commentResult = await createNewComment(taskData?.id, {
            Type: COMMENT_TYPE.CHANGE_GROUP,
            PreviousGroup: previousGroup?.name,
            Group: group?.name
          });
          const newComment = {
            id: nanoid(),
            type: COMMENT_TYPE.CHANGE_GROUP,
            previousGroup: previousGroup?.name,
            group: group?.name
          };
          const updatedComment = getRawComment(newComment, currentUser);
          if (result && commentResult) {
            taskData?.setGroup(group?.id);
            taskData?.upsertComment(updatedComment);
            trackAnalyticActivity(TASK_ANALYTICS.GROUP_CHANGED);
            closeAllDropdowns();
          }
        }
      },
      [closeAllDropdowns, currentProject?.taskGroups, currentUser, isTemplateProject, taskData]
    );

    const removeParentTask = useCallback(async () => {
      const payloadTask = { parentUnset: true, projectId: taskData?.projectId };
      const result = await updateTaskDetails(taskData?.id, payloadTask);
      if (result) {
        taskData?.setParentTask(null);
        loadData?.();
        closeAllDropdowns();
      }
    }, [closeAllDropdowns, taskData, loadData]);

    const createSubTask = useCallback(async () => {
      rootStore.taskStore.createTaskInput.setProject(taskData?.projectId || '');
      rootStore.taskStore.createTaskInput.setParentTask(taskData?.id);
      setOpenCreateTaskModel(true);
    }, [taskData, setOpenCreateTaskModel]);

    const onClickUpdatePriority = useCallback(
      async (key: number) => {
        if (!currentUser) return;
        if (isRecurringTask) {
          taskData?.setPriority(key);
          const payload = { priority: Number(key), projectId: taskData?.projectId };
          const response = await updateRecurringTaskDetails(taskData?.id, payload);
          if (response) {
            trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED);
          }
        } else {
          const newComment = {
            id: nanoid(),
            type: COMMENT_TYPE.CHANGE_PRIORITY,
            previousPriority: taskData?.priority || TASK_PRIORITY.NO_PRIORITY,
            priority: key
          };
          taskData.setPriority(key);
          if (!isTemplateProject) {
            const updatedComment = getRawComment(newComment, currentUser);
            taskData.upsertComment(updatedComment);
            const payloadTask = { priority: Number(key), projectId: taskData?.projectId };
            const result = await updateTaskDetails(taskData?.id, payloadTask);
            const commentResult = await createNewComment(taskData?.id, {
              Type: COMMENT_TYPE.CHANGE_PRIORITY,
              PreviousPriority: newComment?.previousPriority,
              Priority: Number(key)
            });
            if (result && commentResult) {
              trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED);
              closeAllDropdowns();
            }
          } else {
            const updatedTaskList = currentProject?.tasks.map((item) => {
              if (item?.id === taskData?.id) {
                item?.setPriority(key);
              }
              return item;
            });
            rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(updatedTaskList || []);
          }
        }
      },
      [closeAllDropdowns, currentProject?.tasks, currentUser, isRecurringTask, isTemplateProject, taskData]
    );

    const onViewClick = useCallback(() => {
      if (onClickView) onClickView(taskData);
      closeAllDropdowns();
    }, [closeAllDropdowns, onClickView, taskData]);

    const onCloneClick = useCallback(() => {
      if (onClickClone) onClickClone(taskData);
      closeAllDropdowns();
    }, [closeAllDropdowns, onClickClone, taskData]);

    const onEditClick = useCallback(() => {
      if (onClickEdit) onClickEdit(taskData);
      closeAllDropdowns();
    }, [closeAllDropdowns, onClickEdit, taskData]);

    const onDeleteClick = useCallback(() => {
      if (onClickDelete) onClickDelete(taskData);
      closeAllDropdowns();
    }, [closeAllDropdowns, onClickDelete, taskData]);

    const updateTaskMilestone = useCallback(
      async (milestone: Milestone) => {
        if (!currentUser) return;
        const payloadTask = { milestoneId: milestone?.id, projectId: taskData?.projectId };
        taskData?.setMilestone(milestone?.id);
        const result = await updateTaskDetails(taskData?.id, payloadTask);
        const newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.SET_MILESTONE,
          group: milestone?.milestoneName
        };
        const updatedComment = getRawComment(newComment, currentUser);
        taskData?.upsertComment(updatedComment);
        const commentResult = await createNewComment(taskData?.id, {
          Type: COMMENT_TYPE.SET_MILESTONE,
          Group: milestone?.milestoneName
        });
        if (result && commentResult) {
          trackAnalyticActivity(TASK_ANALYTICS.MILESTONE_SELECTED);
          closeAllDropdowns();
        }
      },
      [currentUser, taskData, closeAllDropdowns]
    );

    const removeTaskMilestone = useCallback(async () => {
      if (!isEmpty(taskData?.milestoneId)) {
        taskData?.setMilestone(null);
        const payloadTask = { milestoneIdUnset: true, projectId: taskData?.projectId };
        const result = await updateTaskDetails(taskData?.id, payloadTask);
        if (result) {
          closeAllDropdowns();
        }
      }
    }, [closeAllDropdowns, taskData]);

    return (
      <>
        {!isView && !isMyTask && currentProject?.isGroupEnabled && (
          <Dropdown
            content={
              <ItemDiv>
                <DropdownItem
                  label='Group'
                  iconName='updated-group-icon'
                  iconSize={16}
                  iconViewBox='0 0 18 18'
                  iconTone='fill'
                />
              </ItemDiv>
            }
            trigger='hover'>
            {currentProject?.taskGroups?.map((item) => (
              <DropdownItem
                key={item.index}
                label={item.name}
                onClick={() => {
                  onClickGroup(item);
                }}
              />
            ))}
          </Dropdown>
        )}
        {!isRecurringTask && currentProject?.statusEnable && statusList?.length > 0 && (
          <Dropdown
            content={
              <ItemDiv>
                <DropdownItem label='Status' iconName='task-todo-icon' iconSize={16} iconTone='fill' />
              </ItemDiv>
            }
            trigger='hover'>
            {statusList.map((item, index) => (
              <DropdownItem
                key={index}
                label={item.Name}
                onClick={() => onClickUpdateStatus(item, currentStatus)}
                iconName={renderStatusIconName(item.Type)}
              />
            ))}
          </Dropdown>
        )}
        {(currentProject?.priority?.enabled || isMyTask === true) && (
          <Dropdown
            content={
              <ItemDiv>
                <DropdownItem label='Priority' iconName='Priority-High-icon' iconSize={16} iconTone='fill' />
              </ItemDiv>
            }
            trigger='hover'>
            {menuItems.map((item, index) => (
              <DropdownItem
                key={index}
                label={item.label}
                onClick={() => onClickUpdatePriority(item.key)}
                iconName={item.icon}
              />
            ))}
          </Dropdown>
        )}
        {!isView && currentProject?.isMilestone && !isMyTask && (
          <Dropdown
            content={
              <ItemDiv>
                <DropdownItem
                  label='Milestone'
                  iconName='milestone-icon'
                  iconSize={16}
                  iconViewBox='0 0 18 18'
                  iconTone='fill'
                />
              </ItemDiv>
            }
            trigger='hover'>
            <DropdownItem label='No Milestone' onClick={removeTaskMilestone} />
            {currentProject?.milestones?.map((item) => (
              <DropdownItem key={item?.id} label={item.milestoneName} onClick={() => updateTaskMilestone(item)} />
            ))}
          </Dropdown>
        )}

        <AssignBoxV2
          projectUsers={currentProject?.users!}
          context={{ mode: 'task', currentTask: taskData }}
          onAssign={assignActions.onAssign}
          onUnassign={assignActions.onUnassign}
          isContextMenu={true}
          isMultiAssignee={currentProject?.multiAssignee}
        />

        {currentProject?.labelsEnabled && (
          <LabelDropdownV2
            taskData={taskData}
            currentProject={currentProject}
            isContextMenu={true}
            isTemplateProject={isTemplateProject}
            isRecurringTask={isRecurringTask}
          />
        )}

        {!isTemplateProject && !isRecurringTask && (
          <Dropdown
            ref={calendarDropdownRef}
            trigger='hover'
            isRemoveMaxHeight={true}
            content={
              <ItemDiv>
                <DropdownItem label='Due date' iconName='calendar-icon' iconSize={16} iconViewBox='0 0 18 18' />
              </ItemDiv>
            }>
            <CalendarDropdownMenuV2
              isRecurringTask={isRecurringTask}
              taskData={taskData}
              calendarDate={calendarDate}
              setCalendarDate={setCalendarDate}
              loadData={() => {
                closeAllDropdowns();
              }}
              closeContextMenu={closeAllDropdowns}
            />
          </Dropdown>
        )}
        {!isRecurringTask && !isTemplateProject && <DropdownDivider />}
        {!isRecurringTask &&
          !isTemplateProject &&
          (isSubTask ? (
            <DropdownItem
              label='Remove parent task'
              iconName='remove-parent-task-icon'
              iconSize={16}
              iconViewBox='0 0 18 18'
              onClick={removeParentTask}
            />
          ) : (
            !isMilestonePage && (
              <DropdownItem
                label=' Create subtask'
                iconName='create-sub-task-icon'
                iconSize={16}
                iconViewBox='0 0 18 18'
                onClick={createSubTask}
              />
            )
          ))}
        {!isRecurringTask && <DropdownDivider />}
        {!isRecurringTask && !isTemplateProject && (
          <DropdownItem onClick={onViewClick} label='View' iconName='view-icon' iconViewBox='0 0 18 18' />
        )}
        {!isRecurringTask && (
          <DropdownItem
            onClick={onCloneClick}
            label='Clone'
            iconName='copy-icon'
            iconTone='fill'
            iconViewBox='0 0 18 18'
          />
        )}
        <DropdownItem onClick={onEditClick} label='Edit' iconName='edit-icon' iconTone='fill' iconViewBox='0 0 18 18' />
        <DropdownItem
          onClick={onDeleteClick}
          label='Delete'
          tone='critical'
          iconName='updated-delete-icon'
          iconSize={16}
          iconViewBox='0 0 18 18'
        />
      </>
    );
  }
);

interface CustomContextMenuProps {
  taskData: TaskModel;
  loadData: () => void;
  currentProject?: ProjectModel | ProjectTemplateModel;
  setCurrentProject?: () => void;
  setCurrentActiveTask?: (taskId: string) => void;
  onClickView?: (task: TaskModel) => void;
  onClickClone?: (task: TaskModel) => void;
  onClickEdit?: (task: TaskModel) => void;
  onClickDelete?: (task: TaskModel) => void;
  onUpdateStatus?: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  isMyTask?: boolean;
  children: React.ReactNode;
  statusList: StatusListInterface[];
  isRecurringTask?: boolean;
  isSubTask?: boolean;
  // for hide create subtask option in milestone page
  isMilestonePage?: boolean;
  isMember?: boolean;
  isTemplateProject?: boolean;
  isView?: boolean;
}
const CustomContextMenu: React.FC<CustomContextMenuProps> = ({
  taskData,
  currentProject,
  setCurrentProject,
  loadData,
  setCurrentActiveTask,
  onClickView,
  onClickClone,
  onClickEdit,
  onClickDelete,
  onUpdateStatus,
  isMyTask = false,
  children,
  statusList,
  isRecurringTask,
  isSubTask = false,
  isMilestonePage = false,
  isTemplateProject = false,
  isView = false
}) => {
  const [openCreateTaskModel, setOpenCreateTaskModel] = useState(false);
  const contextMenuRef = useRef<DropdownRef>(null);

  const onOpenChange = useCallback(
    (value: boolean) => {
      if (value) {
        if (setCurrentActiveTask) setCurrentActiveTask(taskData?.id);
        if (setCurrentProject) setCurrentProject();
      } else {
        if (setCurrentActiveTask) {
          setCurrentActiveTask('');
        }
      }
    },
    [setCurrentActiveTask, setCurrentProject, taskData?.id]
  );

  const handleCancel = useCallback(() => {
    setOpenCreateTaskModel(false);
  }, []);

  const handleUpdateStatus = useCallback(
    (status: StatusListInterface, previousStatus: StatusListInterface) => {
      if (onUpdateStatus) onUpdateStatus(taskData, status, previousStatus);
    },
    [onUpdateStatus, taskData]
  );

  return (
    <>
      <Dropdown
        ref={contextMenuRef}
        setCurrentProject={setCurrentProject}
        content={children}
        trigger='contextmenu'
        isRemoveMaxHeight={true}
        isMinWidth={168}
        updateDropdownOpen={onOpenChange}>
        <ContextMenuContent
          taskData={taskData}
          currentProject={currentProject}
          loadData={loadData}
          setCurrentActiveTask={setCurrentActiveTask}
          onClickView={onClickView}
          onClickClone={onClickClone}
          onClickEdit={onClickEdit}
          onClickDelete={onClickDelete}
          onUpdateStatus={handleUpdateStatus}
          isMyTask={isMyTask}
          statusList={statusList}
          isRecurringTask={isRecurringTask}
          isSubTask={isSubTask}
          isMilestonePage={isMilestonePage}
          isTemplateProject={isTemplateProject}
          isView={isView}
          contextMenuRef={contextMenuRef}
          onOpenChange={onOpenChange}
          setOpenCreateTaskModel={setOpenCreateTaskModel}
        />
      </Dropdown>
      <Modal open={openCreateTaskModel} maxWidth={'680px'} onClose={handleCancel} isCreateTask={true}>
        <CreateTaskModalV2 loadData={loadData} onCancel={handleCancel} context={{ source: 'taskDetail' }} />
      </Modal>
    </>
  );
};

export default observer(CustomContextMenu);
